/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement, useState } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Stack,
  StackItem,
  Text,
  Label,
  TextField,
  Layer,
  Popup,
  Overlay,
  FocusTrapZone,
  DefaultButton,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { mvpProgramProfileRetireStatuses } from "Constants/ProfileConstants";
import {
  fetchProgramProfile,
  updateMVPRetireProfileReset,
  retireProfile,
} from "Dataflows/MVP/MVPAction";
import {
  getRetireProfileErrorMessage,
  getRetireProfileOperationStatus,
} from "Dataflows/MVP/RetireProfile/IMVPRetireProfileSelector";

import { IMostValuableProfessionalRetireEditFormProps } from "./IMostValuableProfessionalRetireEditFormProps";
import { labelStylesNormal } from "Components/MostValuableProfessionalProfile/MostValuableProfessionalProfile.styles";
import {
  deleteButtonDangerStyles,
  popupContentStyles,
  popupHorizontalGapStackTokens,
  popupStyles,
  popupTitleDangerStyles,
} from "Components/ConfirmationPopup/ConfirmationPopup.styles";

export const MostValuableProfessionalRetireEditForm = (
  props: IMostValuableProfessionalRetireEditFormProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, defaultValues } = props;
  const [customErrorMessage, setCustomErrorMessage] = useState("");
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [additionalInformation, setAdditionalInformation] = useState(
    defaultValues.additionalInformation
  );

  const {
    control,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const programProfileErrorMessage = useSelector(getRetireProfileErrorMessage);

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(updateMVPRetireProfileReset());
  };

  const onRetire = () => {
    return dispatch(retireProfile(defaultValues));
  };

  const dismissConfirmationModal = () => {
    setIsConfirmationOpen(false);
  };

  const showConfirmationModal = () => {
    setCustomErrorMessage("");
    if (!defaultValues.userProfileId) {
      setCustomErrorMessage("User profile id is required");
    } else if (!defaultValues.reasonForRemoval) {
      setCustomErrorMessage("Reason for removal is required");
    } else if (defaultValues.userProfileId && defaultValues.reasonForRemoval) {
      setIsConfirmationOpen(true);
    }
  };

  const operationStatus = useSelector(getRetireProfileOperationStatus);

  useEffect(() => {
    if (operationStatus.update === OperationStatus.Succeeded) {
      dispatch(fetchProgramProfile(defaultValues.userProfileId as number));
      dismissPanel();
    }
  }, [operationStatus.update === OperationStatus.Succeeded]);

  const isSaving = operationStatus.update === OperationStatus.Pending;

  return (
    <SidePanel
      isOpen={isPanelOpen}
      title="Retire a member"
      saveButtonLabel="Save"
      error={customErrorMessage || programProfileErrorMessage}
      disableActions={isSaving}
      onDismiss={dismissPanel}
      onSave={(): void => {
        showConfirmationModal();
      }}
    >
      <StackItem align="start">
        <Text>
          Retiring a member will revoke their access to the active program experience, cancel any
          system managed benefit and remove the user from all distribution lists.
        </Text>
      </StackItem>
      <StackItem>
        <Stack tokens={{ childrenGap: "1rem" }} horizontal={true} horizontalAlign="space-evenly">
          <StackItem align="center" grow={100}>
            <Label htmlFor="retireStatus" required={true} styles={labelStylesNormal}>
              Reason for retirement
            </Label>
          </StackItem>
          <StackItem align="start" grow={100}>
            <ComboBoxController
              onChange={(_e, op): void => {
                defaultValues.reasonForRemoval = _e.key as string;
              }}
              control={control}
              errors={errors}
              inputName="retireStatus"
              options={mvpProgramProfileRetireStatuses}
              isDisabled={isSaving}
            />
          </StackItem>
        </Stack>
      </StackItem>
      <StackItem>
        <Label htmlFor="retireStatus" styles={labelStylesNormal}>
          Additional Information:
        </Label>
      </StackItem>
      <StackItem>
        <TextField
          multiline
          resizable={false}
          rows={10}
          value={additionalInformation}
          onChange={(_, newValue) => {
            setAdditionalInformation(newValue);
            defaultValues.additionalInformation = newValue;
          }}
        />
      </StackItem>
      {isConfirmationOpen && (
        <Layer>
          <Popup
            className={popupStyles.root}
            role="dialog"
            aria-modal="true"
            onDismiss={dismissConfirmationModal}
          >
            <Overlay onClick={dismissConfirmationModal} />
            <FocusTrapZone>
              <div role="document" className={popupStyles.contentDanger}>
                <Stack tokens={popupHorizontalGapStackTokens}>
                  <Stack.Item>
                    <Text styles={popupTitleDangerStyles}>Remove user data</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text styles={popupContentStyles}>
                      This will remove the user from the program, cancel their system-managed
                      benefits and delete their data. This is an irreversible action, Are you sure
                      want to proceed?
                    </Text>
                  </Stack.Item>
                  <Stack horizontal horizontalAlign="end">
                    <PrimaryButton
                      onClick={onRetire}
                      styles={deleteButtonDangerStyles}
                      disabled={isSaving}
                    >
                      {isSaving ? <Spinner size={SpinnerSize.small} /> : "Yes"}
                    </PrimaryButton>
                    <DefaultButton disabled={isSaving} onClick={dismissConfirmationModal}>
                      Cancel
                    </DefaultButton>
                  </Stack>
                </Stack>
              </div>
            </FocusTrapZone>
          </Popup>
        </Layer>
      )}
    </SidePanel>
  );
};
